import React, { useEffect } from 'react'

import {
  scenarioDateComparator,
  scenarioDateFilterParams,
  scenarioSummaryFilterParams
} from 'components/AgGridCustomFilters/helpers'
import { DATE_FORMAT, FIXED_COLUMNS_SCENARIO } from 'pages/Scenario/constants'
import { commonColumnProps, getFilterParams } from 'pages/Scenario/utils/table'

import { Loading } from '@ublique/components/Loading'
import CustomDateComponent from 'components/AgGridCustomFilters/CustomDateComponent'

import DateColumnFramework from 'pages/Scenario/Manager/components/DateColumnFramework'
import TitleColumnFramework from 'pages/Scenario/Manager/components/TitleColumnFramework'
import StatusColumnFramework from 'pages/Scenario/Manager/components/StatusColumnFramework'
import SummaryColumnFramework from 'pages/Scenario/Manager/components/SummaryColumnFramework'
import ProgressColumnFramework from 'pages/Scenario/Manager/components/ProgressColumnFramework'
import TranslateColumnFramework from 'pages/Scenario/Manager/components/TranslateColumnFramework'
import { filter } from 'lodash'

export const getScenarioColumns = (t) => [
  {
    ...commonColumnProps,
    headerName: t('Table.Type'),
    field: 'type',
    colId: 'type',
    wrapText: true,
    minWidth: 200,
    cellRenderer: 'translateRenderer'
  },
  {
    ...commonColumnProps,
    headerName: t('Table.User'),
    field: 'username',
    colId: 'username',
    minWidth: 130
  },
  {
    ...commonColumnProps,
    headerName: t('Table.Summary'),
    field: 'summary',
    colId: 'summary',
    cellRenderer: 'summaryRenderer',
    minWidth: 250,
    filterParams: scenarioSummaryFilterParams
  },
  {
    ...commonColumnProps,
    headerName: t('Table.MasterDataName'),
    field: 'masterdataName',
    colId: 'masterdataName',
    minWidth: 180,
    filterParams: getFilterParams(t)
  },
  {
    ...commonColumnProps,
    headerName: t('Table.ParentName'),
    field: 'parentName',
    colId: 'parentName',
    minWidth: 180,
    filterParams: getFilterParams(t),
    valueFormatter: ({ data: { masterdataName, parentName }, value }) => {
      return masterdataName === parentName ? '' : value
    }
  },
  {
    ...commonColumnProps,
    headerName: t('Table.CreationData'),
    headerTooltip: t('Table.CreationData'),
    field: 'creationDateTime',
    colId: 'creationDateTime',
    minWidth: 200,
    filter: 'agDateColumnFilter',
    cellRenderer: 'dateRenderer',
    comparator: scenarioDateComparator(DATE_FORMAT),
    filterParams: scenarioDateFilterParams(DATE_FORMAT),
    cellRendererParams: {
      showTime: true
    }
  },
  {
    ...commonColumnProps,
    headerName: t('Table.StartData'),
    headerTooltip: t('Table.StartData'),
    field: 'startDateTime',
    colId: 'startDateTime',
    minWidth: 190,
    filter: 'agDateColumnFilter',
    cellRenderer: 'dateRenderer',
    comparator: scenarioDateComparator(DATE_FORMAT),
    filterParams: scenarioDateFilterParams(DATE_FORMAT),
    cellRendererParams: {
      showTime: true
    }
  },
  {
    ...commonColumnProps,
    headerName: t('Table.EndData'),
    headerTooltip: t('Table.EndData'),
    field: 'endDateTime',
    colId: 'endDateTime',
    minWidth: 180,
    filter: 'agDateColumnFilter',
    cellRenderer: 'dateRenderer',
    comparator: scenarioDateComparator(DATE_FORMAT),
    filterParams: scenarioDateFilterParams(DATE_FORMAT),
    cellRendererParams: {
      showTime: true
    }
  },
  {
    ...commonColumnProps,
    headerName: t('Table.ConsolidationDate'),
    headerTooltip: t('Table.ConsolidationDate'),
    field: 'consolidated',
    colId: 'consolidated',
    minWidth: 190,
    filter: 'agDateColumnFilter',
    cellRenderer: 'dateRenderer',
    comparator: scenarioDateComparator(DATE_FORMAT),
    filterParams: scenarioDateFilterParams(DATE_FORMAT),
    cellRendererParams: {
      showTime: true
    }
  }
]

export const getStandardColumnDefs = (t) => [
  {
    ...commonColumnProps,
    headerName: t('Table.Name'),
    field: 'name',
    colId: 'name',
    minWidth: 300,
    pinned: 'left',
    tooltipField: 'name',
    cellRenderer: 'nameRenderer',
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    lockVisible: true,
    cellClassRules: {
      'warnig-scenario-cell': (params) => params.data.warning,
      'cancel-scenario-cell': (params) => params.data.cancel
    }
  },
  {
    ...commonColumnProps,
    headerName: t('Table.Status'),
    field: 'status',
    colId: 'status',
    pinned: 'right',
    minWidth: 180,
    cellRenderer: 'statusRenderer',
    filterParams: getFilterParams(t),
    lockVisible: true
  },
  {
    suppressMovable: true,
    headerName: t('Table.Progress'),
    field: 'progress',
    colId: 'progress',
    pinned: 'right',
    minWidth: 200,
    cellRenderer: 'progressRenderer',
    floatingFilter: false,
    lockVisible: true
  }
]

export const columnFrameworks = {
  // Custom loading
  customLoadingOverlay: () => <Loading />,

  // Custom columns
  dateRenderer: DateColumnFramework,
  nameRenderer: TitleColumnFramework,
  statusRenderer: StatusColumnFramework,
  summaryRenderer: SummaryColumnFramework,
  progressRenderer: ProgressColumnFramework,
  translateRenderer: TranslateColumnFramework,

  // Date custom
  agDateInput: CustomDateComponent
}

// See: https://react.carbondesignsystem.com/?path=/docs/components-overflowmenu--default#data-floating-menu-container
// This is needed for setting the overflow menu parent - AG Grid table root
// The overflow menu body has absolute position
export const useOverflowMenuParent = () => {
  useEffect(() => {
    document
      .querySelector('div.ag-root-wrapper')
      .setAttribute('data-floating-menu-container', '')
  }, [])
}

/// exclude columns in fixed position from lockableColumns
export const getLockableColumns = (columns) => {
  return filter(columns, ({ field }) => !FIXED_COLUMNS_SCENARIO.includes(field))
}
